import React from 'react'
import { Typography } from '@mui/material'
import {
  Page,
  Section,
  Bullets,
  Bullet,
  Info,
  Warning,
  Step,
  Screenshot,
} from '../../components/DocsComponents.jsx'
import DocLink from '../../components/DocLink.jsx'

const InvoicesCreditNotes = () => (
  <Page
    title="Invoices and Credit Notes"
    intro="The Invoices page lists every invoice from Events and Patient Transport in one
    grid. Each invoice moves through a lifecycle — Draft, Approved, Sent, and on to Paid
    or Overdue — and from an individual invoice you can approve, send, record payments,
    download a PDF, and issue full or partial credit notes."
  >
    <Section title="The invoice list">
      <Typography>
        Open <DocLink url="/invoices" text="Invoices" /> from the Finance
        section of the sidebar. The page shows:
      </Typography>
      <Bullets>
        <Bullet>
          A <strong>source toggle</strong> — Both, Events, or PTS — to filter
          the grid to one side of the business.
        </Bullet>
        <Bullet>
          Summary tiles for <strong>Invoice Value</strong>,{' '}
          <strong>Paid</strong>, <strong>Outstanding</strong>, and{' '}
          <strong>Overdue</strong>, calculated across the invoices currently in
          view.
        </Bullet>
        <Bullet>
          A grid with the reference, source, linked record (the event or PTS
          booking, clickable), customer, invoice and due dates, status, total,
          amount paid, and amount still due.
        </Bullet>
      </Bullets>
      <Typography>
        Click any row to open the invoice. The Linked Record button jumps
        straight to the event or PTS booking instead.
      </Typography>
      <Info>
        Totals include VAT, worked out per line item using your organisation's
        region (UK or Ireland rates). An invoice counts as overdue when its
        status is <strong>Overdue</strong>, or when it is Sent or Partially
        Paid and its due date has passed. An invoice that was approved but
        never sent does not count as overdue, however old it is.
      </Info>
      <Screenshot url="finance/invoices-page.png">
        Invoices page with the source toggle, summary tiles, and the invoice
        grid.
      </Screenshot>
    </Section>

    <Section title="The invoice lifecycle">
      <Typography>
        Every invoice — Event or PTS — moves through the same statuses:{' '}
        <strong>Draft</strong> → <strong>Approved</strong> →{' '}
        <strong>Sent</strong> → <strong>Partially Paid</strong> /{' '}
        <strong>Paid</strong> / <strong>Overdue</strong>.
      </Typography>
      <Bullets>
        <Bullet>
          <strong>Draft</strong> — every new invoice starts here. Drafts are
          fully editable (line items, dates, reference) and deletable, and they
          cannot be sent, paid, or exported until approved.
        </Bullet>
        <Bullet>
          <strong>Approved</strong> — a member of the Finance team has reviewed
          the invoice and fixed its contents. <strong>Approve</strong> is a
          Finance-team action; who approved it, and when, is stamped on the
          invoice. An approved invoice can be reverted to Draft (also Finance
          only), but only while no payments or credit notes have been recorded
          against it.
        </Bullet>
        <Bullet>
          <strong>Sent</strong> — <strong>Send</strong> emails a summary of the
          invoice to the customer and stamps the date and address it went to.
          For organisation-billed invoices the email goes to the organisation's
          finance contact, falling back to the organisation's main email; for
          patient-billed invoices it goes to the patient. After the first send
          the button becomes <strong>Resend</strong>.
        </Bullet>
        <Bullet>
          <strong>Partially Paid / Paid</strong> — set automatically as
          payments are recorded. <strong>Mark Paid</strong> (Finance team)
          records the whole remaining balance as a single payment and settles
          the invoice in one action.
        </Bullet>
        <Bullet>
          <strong>Overdue</strong> — applied automatically. A daily scheduled
          job flips any Sent or Partially Paid invoice with an outstanding
          balance past its due date to Overdue.
        </Bullet>
      </Bullets>
      <Info>
        Overdue invoices are chased automatically as well: the same daily job
        emails a payment reminder to the invoice's recipient (the
        organisation's finance contact, or the patient) at most once every
        seven days, until the balance is cleared. Each reminder is recorded on
        the invoice.
      </Info>
    </Section>

    <Section title="An individual invoice">
      <Typography>
        The invoice page shows four headline figures — Total, Paid, Credited,
        and Outstanding — followed by the invoice details (dates, client, and
        the linked event or PTS booking), the line items with VAT, all payments
        recorded so far, and any credit notes issued. The action buttons change
        with the invoice's status:
      </Typography>
      <Bullets>
        <Bullet>
          <strong>Approve</strong> — Draft invoices only; moves the invoice to
          Approved (Finance team).
        </Bullet>
        <Bullet>
          <strong>Edit Invoice</strong> and <strong>Delete Draft</strong> —
          Draft invoices only. Editing opens the invoice editor to change line
          items, dates, and the reference; once an invoice is approved, use a
          credit note instead. Deleting a draft leaves a gap in the reference
          sequence — reference numbers are never reused.
        </Bullet>
        <Bullet>
          <strong>Send / Resend</strong> — any approved invoice; emails the
          invoice summary as described above.
        </Bullet>
        <Bullet>
          <strong>Record Payment</strong> and <strong>Mark Paid</strong> — any
          issued invoice that is not yet Paid; see{' '}
          <DocLink
            url="/finance/payments-and-unpaid-invoices"
            text="Payments and Unpaid Invoices"
            docsPage
          />
          .
        </Bullet>
        <Bullet>
          <strong>Credit Note</strong> and <strong>Reissue</strong> — any
          invoice that has left Draft, including Paid invoices; see below.
        </Bullet>
        <Bullet>
          <strong>Download PDF</strong> — always available; a printable PDF of
          the invoice.
        </Bullet>
      </Bullets>
      <Warning>
        Once approved, an invoice is a fixed document: corrections are made
        with a credit note, and <strong>Reissue</strong> credits the remainder
        in full and creates a new draft copy to amend. An invoice marked{' '}
        <strong>Paid</strong> can no longer take payments, but Credit Note and
        Reissue remain available — for example to refund work that was paid and
        then cancelled. For how PTS invoices are priced and billed, see{' '}
        <DocLink
          url="/patient-transport/invoicing"
          text="PTS Quoting, Invoicing & Billing"
          docsPage
        />
        .
      </Warning>
      <Screenshot url="finance/invoice-detail.png">
        A single invoice with the four totals, action buttons, and the items
        table.
      </Screenshot>
    </Section>

    <Section title="Issuing a credit note">
      <Typography>
        Use a credit note to reduce what a customer owes after an invoice has
        been issued — for a cancelled shift, an over-charge, or a goodwill
        adjustment — without deleting the invoice or losing the audit trail.
        Credit notes work on any invoice that has left Draft, whether it came
        from an event or a PTS booking.
      </Typography>
      <Step number={1} title="Open the invoice and choose Credit Note">
        <Typography>
          The dialog shows how much is left to credit, taking any earlier
          credit notes into account.
        </Typography>
      </Step>
      <Step number={2} title="Choose full or partial credit">
        <Typography>
          <strong>Full credit</strong> credits the whole remaining value of the
          invoice. <strong>Partial credit</strong> lets you pick the lines,
          quantities, and prices to credit back.
        </Typography>
      </Step>
      <Step number={3} title="Give a reason and submit">
        <Typography>
          The reason is stored on the credit note and shown on the invoice. The
          credit note is given the next reference automatically (CN-00001,
          CN-00002, …) and the action is written to the invoice's log.
        </Typography>
      </Step>
      <Info>
        You cannot credit more than the invoice's remaining creditable value —
        the server validates this, so over-crediting is blocked even if the
        figures are typed by hand.
      </Info>
    </Section>

    <Section title="Who can do this?">
      <Bullets>
        <Bullet>
          <strong>Invoice list</strong> — Finance team.
        </Bullet>
        <Bullet>
          <strong>Opening an individual invoice</strong> — Event Planning team{' '}
          <em>or</em> PTS Co-Ordinator access (PTS co-ordinators land on this
          page from the PTS invoice lists).
        </Bullet>
        <Bullet>
          <strong>Creating and editing draft invoices, deleting drafts, and
          sending</strong>{' '}
          — Event Planning team or PTS Co-Ordinator access.
        </Bullet>
        <Bullet>
          <strong>Approving, reverting to Draft, recording payments, Mark
          Paid, credit notes, and Reissue</strong>{' '}
          — Finance team.
        </Bullet>
      </Bullets>
      <Warning>
        Because the invoice list needs the Finance team and the invoice detail
        page needs the Event Planning team (or PTS Co-Ordinator access), staff
        who work invoices end-to-end need <strong>both</strong> on their
        employee record.
      </Warning>
    </Section>
  </Page>
)

export default InvoicesCreditNotes
